import React, {useState} from 'react';
import {browserHistory, Link} from 'react-router';
import {List, Map, Set} from 'immutable';
import classnames from 'classnames';
import upperFirst from 'lodash/upperFirst';
import noop from 'lodash/noop';

import Checkbox from '@material-ui/core/Checkbox';
import Menu from '@material-ui/core/Menu';
import MenuItem from '@material-ui/core/MenuItem';

import {continuityString, KpiAssignment, KpiTarget, KpiTargetId} from '@store/@types/kpi';
import User, {UserId} from 'records/User';

import CogIcon from 'components/pure/utils/icon/CogIcon';
import UserAvatar from 'components/pure/utils/UserAvatar';
import OverflowTooltip from 'components/pure/utils/OverflowTooltip';
import {KpiBulbIcon} from '@components/icon/KpiBulbIcon';
import {BullseyeIcon} from '@components/icon/BullseyeIcon';

import confirm from 'utils/confirm';
import GenericTooltip from 'components/pure/tooltips/GenericTooltip';
import KpiGoalActions from '@store/kpi/KpiTargetActions';

export interface Props {
  users: Map<UserId, User>;
  kpiTarget: KpiTarget;
  kpiChildren?: List<KpiTarget>;
  isChild?: boolean;
  isLast?: boolean;
  selectedKpis: Set<KpiTargetId>;
  selectedGoal?: KpiTargetId;
  deleteKpi: (id: KpiTargetId) => Promise<void>;
  assignmentsByKpiId: Map<KpiTargetId, List<KpiAssignment>>;
  parent?: KpiTarget;
  toggleKpi: typeof KpiGoalActions.toggleKpi;
}

export const GoalKpiRow: React.FC<Props> = ({
  users,
  kpiTarget,
  kpiChildren = List(),
  isChild,
  isLast,
  selectedKpis,
  selectedGoal,
  deleteKpi,
  toggleKpi,
  assignmentsByKpiId,
  parent,
}) => {
  const [menuRef, setMenuRef] = useState<Element | null>(null);
  const editPath = `/admin/kpis/edit/${kpiTarget.id}`;
  const editHandler = () => browserHistory.push(editPath);
  const deleteHandler = () =>
    confirm(`Are you sure you want to delete the KPI: ${kpiTarget.name}?`).then(() => {
      deleteKpi(kpiTarget.id).then().catch(console.error);
    }, noop);
  const isGoal = !kpiChildren.isEmpty();
  const allowDelete =
    !assignmentsByKpiId.has(kpiTarget.id) && (!parent || !assignmentsByKpiId.has(parent.id));

  const user = users.get(kpiTarget.createdByUserId);
  return (
    <>
      <tr
        className={classnames(`goal-kpi-row`, {
          'child-row': isChild,
          'last-row': isLast,
          'parent-row': isGoal,
        })}>
        <td className="goal-kpi">
          <Checkbox
            color="primary"
            className="checkbox"
            checked={isGoal ? selectedGoal === kpiTarget.id : selectedKpis.has(kpiTarget.id)}
            onClick={() => toggleKpi(kpiTarget.id)}
          />
          <GenericTooltip inline tooltip={isGoal ? 'Goal' : 'KPI'}>
            {isGoal ? <BullseyeIcon /> : <KpiBulbIcon />}
          </GenericTooltip>
          <Link
            to={{
              pathname: editPath,
              query: {tabIndex: 0},
            }}>
            <OverflowTooltip tooltipText={kpiTarget.name} divKey={'kpi' + kpiTarget.id}>
              <div className="kpi-name">{kpiTarget.name}</div>
            </OverflowTooltip>
          </Link>
        </td>
        <td className="owner">
          <div>
            <UserAvatar user={user} />
            <div className="username">{user?.getDisplayName()}</div>
          </div>
        </td>
        <td className="frequency">{upperFirst(kpiTarget.frequency.toLowerCase())}</td>
        <td className="continuity">{continuityString(kpiTarget.continuity)}</td>
        <td className="cog pointer">
          <CogIcon onClick={e => setMenuRef(e.currentTarget)} />
          <Menu
            onClose={() => setMenuRef(null)}
            anchorEl={menuRef}
            open={!!menuRef}
            className="action-menu">
            <MenuItem onClick={editHandler}>Edit</MenuItem>
            {allowDelete ? (
              <MenuItem onClick={deleteHandler}>Delete</MenuItem>
            ) : (
              <GenericTooltip tooltip="In use in active assignments">
                <MenuItem disabled>Delete</MenuItem>
              </GenericTooltip>
            )}
          </Menu>
        </td>
      </tr>
      {kpiChildren.map((child, index) => (
        <GoalKpiRow
          selectedKpis={selectedKpis}
          users={users}
          kpiTarget={child}
          isChild={true}
          isLast={index + 1 === kpiChildren.size}
          key={child.id}
          toggleKpi={toggleKpi}
          deleteKpi={deleteKpi}
          assignmentsByKpiId={assignmentsByKpiId}
          parent={kpiTarget}
        />
      ))}
    </>
  );
};
